import { ref } from 'vue'
import { defineStore } from 'pinia'
import { useNotificationStore } from './notification'
import { t } from '@/utils/i18nHelper'

export const useAlertStore = defineStore('alert', () => {
  // ========== MODAL ALERTS (centro da tela) ==========
  const show = ref(false)
  const title = ref('')
  const message = ref('')
  const type = ref('info') // success, error, warning, info
  const confirmCallback = ref(null)
  const cancelCallback = ref(null)
  const showCancel = ref(false)
  
  // ========== TOAST NOTIFICATIONS (canto superior direito) ==========
  const toasts = ref([])
  let toastIdCounter = 0
  
  function alert(msg, options = {}) {
    title.value = options.title || t('common.warning')
    message.value = msg
    type.value = options.type || 'info'
    showCancel.value = false
    show.value = true
    
    return new Promise((resolve) => {
      confirmCallback.value = () => {
        show.value = false
        resolve(true)
      }
    })
  }
  
  function confirm(msg, options = {}) {
    title.value = options.title || t('common.confirm')
    message.value = msg
    type.value = options.type || 'warning'
    showCancel.value = true
    show.value = true
    
    return new Promise((resolve) => {
      confirmCallback.value = () => {
        show.value = false
        resolve(true)
      }
      
      cancelCallback.value = () => {
        show.value = false
        resolve(false)
      }
    })
  }
  
  function success(msg, titleOverride) {
    return alert(msg, { type: 'success', title: titleOverride || t('common.success') })
  }
  
  function error(msg, titleOverride) {
    return alert(msg, { type: 'error', title: titleOverride || t('common.error') })
  }
  
  function warning(msg, titleOverride) {
    return alert(msg, { type: 'warning', title: titleOverride || t('common.warning') })
  }
  
  function info(msg, titleOverride) {
    return alert(msg, { type: 'info', title: titleOverride || t('common.info') })
  }
  
  function close() {
    show.value = false
  }
  
  // ========== TOAST NOTIFICATIONS ==========
  
  /**
   * Cria um toast (notificação no canto superior direito)
   * @param {string} msg - Mensagem
   * @param {object} options - { type: 'success'|'error'|'warning'|'info'|'loading', title: string, duration: number, persistent: boolean }
   * @returns {number} toastId - ID do toast (para remover depois)
   */
  function toast(msg, options = {}) {
    const id = ++toastIdCounter
    const toastType = options.type || 'info'
    const duration = options.duration !== undefined ? options.duration : (toastType === 'loading' ? 0 : 4000)
    const persistent = options.persistent || toastType === 'loading'
    
    toasts.value.push({
      id,
      title: options.title || getTitleByType(toastType),
      message: msg,
      type: toastType,
      duration,
      persistent,
      show: true
    })
    
    // Auto-remover após duração (se não for persistente)
    if (!persistent && duration > 0) {
      setTimeout(() => {
        removeToast(id)
      }, duration)
    }
    
    return id
  }
  
  /**
   * Remove um toast específico
   */
  function removeToast(id) {
    const index = toasts.value.findIndex(t => t.id === id)
    if (index !== -1) {
      toasts.value.splice(index, 1)
    }
  }
  
  /**
   * Remove todos os toasts
   */
  function clearToasts() {
    toasts.value = []
  }
  
  /**
   * Toast de sucesso
   */
  function toastSuccess(msg, options = {}) {
    const toastId = toast(msg, { ...options, type: 'success' })
    
    // ✅ Adicionar ao histórico de notificações (se saveToHistory = true)
    if (options.saveToHistory !== false) {
      const notificationStore = useNotificationStore()
      notificationStore.add({
        title: options.title || `✅ ${t('common.success')}`,
        message: msg,
        type: 'success',
        data: options.data
      })
    }
    
    return toastId
  }
  
  /**
   * Toast de erro
   */
  function toastError(msg, options = {}) {
    const toastId = toast(msg, { ...options, type: 'error' })
    
    // ✅ Adicionar ao histórico de notificações (se saveToHistory = true)
    if (options.saveToHistory !== false) {
      const notificationStore = useNotificationStore()
      notificationStore.add({
        title: options.title || `❌ ${t('common.error')}`,
        message: msg,
        type: 'error',
        data: options.data
      })
    }
    
    return toastId
  }
  
  /**
   * Toast de aviso
   */
  function toastWarning(msg, options = {}) {
    const toastId = toast(msg, { ...options, type: 'warning' })
    
    // ✅ Adicionar ao histórico de notificações (se saveToHistory = true)
    if (options.saveToHistory !== false) {
      const notificationStore = useNotificationStore()
      notificationStore.add({
        title: options.title || `⚠️ ${t('common.warning')}`,
        message: msg,
        type: 'warning',
        data: options.data
      })
    }
    
    return toastId
  }
  
  /**
   * Toast de informação
   */
  function toastInfo(msg, options = {}) {
    const toastId = toast(msg, { ...options, type: 'info' })
    
    // ✅ Adicionar ao histórico de notificações (se saveToHistory = true)
    if (options.saveToHistory !== false) {
      const notificationStore = useNotificationStore()
      notificationStore.add({
        title: options.title || `ℹ️ ${t('common.info')}`,
        message: msg,
        type: 'info',
        data: options.data
      })
    }
    
    return toastId
  }
  
  /**
   * Toast de loading (persiste até ser removido manualmente)
   * NÃO é salvo no histórico (é temporário)
   */
  function toastLoading(msg, options = {}) {
    return toast(msg, { ...options, type: 'loading', persistent: true })
  }
  
  /**
   * Obtém título padrão baseado no tipo
   */
  function getTitleByType(toastType) {
    const titles = {
      success: `✅ ${t('common.success')}`,
      error: `❌ ${t('common.error')}`,
      warning: `⚠️ ${t('common.warning')}`,
      info: `ℹ️ ${t('common.info')}`,
      loading: `⏳ ${t('common.loading')}`
    }
    return titles[toastType] || t('common.info')
  }
  
  return {
    // Modal Alerts
    show,
    title,
    message,
    type,
    showCancel,
    confirmCallback,
    cancelCallback,
    alert,
    confirm,
    success,
    error,
    warning,
    info,
    close,
    
    // Toast Notifications
    toasts,
    toast,
    removeToast,
    clearToasts,
    toastSuccess,
    toastError,
    toastWarning,
    toastInfo,
    toastLoading
  }
})

